// Catálogo centralizado de tipos de canal (id = "tipo" da tabela legacy)
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WidgetsIcon from "@mui/icons-material/Widgets";
import MailIcon from "@mui/icons-material/Mail";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import PhoneIcon from "@mui/icons-material/Phone";
import SmsIcon from "@mui/icons-material/Sms";
import LanguageIcon from "@mui/icons-material/Language";
import ForumIcon from "@mui/icons-material/Forum";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import VideocamIcon from "@mui/icons-material/Videocam";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import {
  WorkspaceSegments,
  normalizeWorkspaceSegment,
} from "./workspaceSegments";

export const CHANNEL_TYPES = {
  DEFAULT: 0,            // Canais genéricos / sem tipo definido
  TWILIO_PHONE: 5,
  WHATSAPP_QR: 6,
  MAILGUN: 9,
  FB_MESSENGER: 12,
  WHATSAPP_GUPSHUP: 20,
  IG_MESSENGER: 21,
  /** Comentários em posts da Página do Facebook (legado xpro tipo 23) */
  FB_COMMENTS: 23,
  WHATSAPP_CLOUD: 24,
  // 6 reservado para WhatsApp QRCode
  TWILIO_SMS: 25,
  APP_IOS: 26,
  APP_ANDROID: 27,
  WORDPRESS: 28,
  PABX: 29,
  SMTP: 30,
  // Canal SMS unificado (provedores internos da plataforma)
  SMS: 31,
  // Alias legado (mantido por compat no frontend)
  CLICKATELL_SMS: 31,
  GOOGLE_ADS: 32,
  META_ADS: 33,
  // Novo tipo reservado p/ Ecommerce
  ECOMMERCE: 40,
  WEBSITE_VIACAO: 41,
  /** RCS Business — credenciais globais na infraestrutura; canal só com parâmetros de roteamento */
  RCS_BUSINESS: 42,
  /** Portal web para clientes acompanharem chamados */
  XPRO_CLIENTE: 43,
  /** Comentários em posts/reels do Instagram Business */
  IG_COMMENTS: 44,
  /** Chamadas de Vídeo (Twilio Video) */
  TWILIO_VIDEO: 45,
  /** Reclame Aqui — scraping (entrada) ou API HugMe (entrada + resposta) */
  RECLAME_AQUI: 46,
  /** Botão de conversa no site do cliente */
  SITE_WEBCHAT: 47,
  /** @deprecated use CHANNEL_TYPES.RCS_BUSINESS */
  RCS_TWILIO: 42,
  // E-mail via SES (AWS) usando a conta Xpro
  EMAIL_AWS_SES: 35,
};

/**
 * Tipos com envio real de mensagem ao cliente no compositor do chat.
 * Exclui DEFAULT (só origem/anotação), Ads, Ecommerce (ingestão) e Vídeo.
 */
export const COMPOSER_OUTBOUND_CHANNEL_TIPOS = new Set([
  CHANNEL_TYPES.TWILIO_PHONE,
  CHANNEL_TYPES.WHATSAPP_QR,
  CHANNEL_TYPES.MAILGUN,
  CHANNEL_TYPES.FB_MESSENGER,
  CHANNEL_TYPES.WHATSAPP_GUPSHUP,
  CHANNEL_TYPES.IG_MESSENGER,
  CHANNEL_TYPES.FB_COMMENTS,
  CHANNEL_TYPES.WHATSAPP_CLOUD,
  CHANNEL_TYPES.TWILIO_SMS,
  CHANNEL_TYPES.APP_IOS,
  CHANNEL_TYPES.APP_ANDROID,
  CHANNEL_TYPES.WORDPRESS,
  CHANNEL_TYPES.PABX,
  CHANNEL_TYPES.SMTP,
  CHANNEL_TYPES.SMS,
  CHANNEL_TYPES.EMAIL_AWS_SES,
  CHANNEL_TYPES.WEBSITE_VIACAO,
  CHANNEL_TYPES.RCS_BUSINESS,
  CHANNEL_TYPES.XPRO_CLIENTE,
  CHANNEL_TYPES.IG_COMMENTS,
  CHANNEL_TYPES.SITE_WEBCHAT,
]);

/** True se o tipo pode aparecer no seletor de envio do chat (além de Anotações internas). */
export function isComposerOutboundChannelTipo(tipo) {
  const t = Number(tipo);
  if (!Number.isFinite(t)) return false;
  return COMPOSER_OUTBOUND_CHANNEL_TIPOS.has(t);
}

/**
 * Outbound no compositor considerando modo do canal (ex.: Reclame Aqui só em modo API).
 * Aceita fallback de `tipo` quando a row do canal ainda não carregou (evita flicker/regressão).
 * @param {object|null|undefined} channel
 * @param {number|string|null|undefined} [tipoFallback]
 */
export function isComposerOutboundChannel(channel, tipoFallback = null) {
  const tipo = Number(
    channel?.tipo != null && channel?.tipo !== "" ? channel.tipo : tipoFallback
  );
  if (!Number.isFinite(tipo)) return false;
  if (tipo === CHANNEL_TYPES.RECLAME_AQUI) {
    // Sem row do canal não dá para saber ra_mode — não tratar como outbound.
    if (!channel) return false;
    return String(channel.ra_mode || "").trim().toLowerCase() === "api";
  }
  return isComposerOutboundChannelTipo(tipo);
}

/**
 * Canal ativo na tela de Canais (origensdeleads.status = 1).
 * Payload sem o campo status é tratado como ativo para não esvaziar o seletor.
 */
export function isActiveChannel(channel) {
  const status = channel?.status;
  if (status === null || status === undefined || status === "") return true;
  return Number(status) === 1;
}

/**
 * Normaliza o rótulo de canal da API para a chave usada no switch do composer.
 * Ex.: API passou a devolver "Telefone" no lugar de "Phone" — o discador depende de "Phone".
 */
export function normalizeComposerChannelType(channelType, tipo = null) {
  const n = Number(tipo);
  if (n === CHANNEL_TYPES.TWILIO_PHONE || n === CHANNEL_TYPES.PABX) return "Phone";
  if (n === CHANNEL_TYPES.TWILIO_VIDEO) return "Vídeo";

  const raw = String(channelType || "").trim();
  if (!raw) return channelType;
  const lower = raw.toLowerCase();
  if (lower === "phone" || lower === "telefone") return "Phone";
  if (lower === "video" || lower === "vídeo" || lower === "video calls") return "Vídeo";
  return channelType;
}

/** WhatsApp por sessão QR (não Cloud API): sem regra de janela de 24h / templates de reengajamento. */
export const isWhatsAppQrChannelTipo = (tipo) => Number(tipo) === CHANNEL_TYPES.WHATSAPP_QR;

/** WhatsApp oficial (Gupshup / Meta Cloud API). */
export const isWhatsAppOfficialChannelTipo = (tipo) =>
  [CHANNEL_TYPES.WHATSAPP_GUPSHUP, CHANNEL_TYPES.WHATSAPP_CLOUD].includes(Number(tipo));

/**
 * Canais que o usuário consegue ligar sozinho no wizard, nesta sessão:
 * sem Meta, operadora, DNS ou compra de número.
 */
export const WIZARD_SELF_SERVE_CHANNEL_TYPES = [
  CHANNEL_TYPES.WHATSAPP_QR,
  CHANNEL_TYPES.EMAIL_AWS_SES,
  CHANNEL_TYPES.XPRO_CLIENTE,
  CHANNEL_TYPES.TWILIO_VIDEO,
  CHANNEL_TYPES.SITE_WEBCHAT,
];

export const CHANNEL_CARDS = [
  // ---- DEFAULT (genérico) ----
  {
    id: CHANNEL_TYPES.DEFAULT,
    key: "default",
    label: "Default",
    description: "Canal genérico (sem integrações específicas)",
    icon: (props) => <WidgetsIcon {...props} />,
  },

//   // ---- WhatsApp ----
//   {
//     id: CHANNEL_TYPES.WHATSAPP_CLOUD,
//     key: "whatsapp_cloud",
//     label: "WhatsApp Cloud API",
//     description: "Canal oficial Meta Business",
//     icon: (props) => <WhatsAppIcon {...props} />,
//     color: "#25D366",
//   },
  {
    id: CHANNEL_TYPES.WHATSAPP_GUPSHUP,
    key: "whatsapp_gupshup",
    label: "WhatsApp Cloud API",
    description:
      "WhatsApp Business Platform (Meta): mensagens, templates e fluxos pela API oficial hospedada.",
    icon: (props) => <WhatsAppIcon {...props} />,
    color: "#22C35E",
  },
  {
    id: CHANNEL_TYPES.WHATSAPP_QR,
    key: "whatsapp_qr",
    label: "WhatsApp (QR Code)",
    description: "Sessão pelo aplicativo via QR Code (não usa Cloud API).",
    icon: (props) => <WhatsAppIcon {...props} />,
    color: "#1FA855",
  },

  // ---- Social ----
  {
    id: CHANNEL_TYPES.FB_MESSENGER,
    key: "facebook_messenger",
    label: "Facebook Messenger",
    description: "Solicitações via Facebook",
    icon: (props) => <FacebookIcon {...props} />,
    color: "#1877F2",
  },
  {
    id: CHANNEL_TYPES.FB_COMMENTS,
    key: "facebook_comments",
    label: "Facebook Comentários",
    description: "Comentários em posts da Página do Facebook",
    icon: (props) => <FacebookIcon {...props} />,
    color: "#1877F2",
  },
  {
    id: CHANNEL_TYPES.IG_MESSENGER,
    key: "instagram_messenger",
    label: "Instagram Direct",
    description: "Mensagens diretas do Instagram",
    icon: (props) => <InstagramIcon {...props} />,
    color: "#C13584",
  },
  {
    id: CHANNEL_TYPES.IG_COMMENTS,
    key: "instagram_comments",
    label: "Instagram Comentários",
    description: "Comentários em posts e reels do Instagram",
    icon: (props) => <InstagramIcon {...props} />,
    color: "#C13584",
  },

  // ---- E-mail ----
  {
    id: CHANNEL_TYPES.EMAIL_AWS_SES,
    key: "email_aws_ses",
    label: "E-mail",
    description: "Envio e recepção de e-mails diretamente pela plataforma, sem precisar configurar servidor de e-mail próprio.",
    icon: (props) => <MailIcon {...props} />,
  },
  {
    id: CHANNEL_TYPES.SMTP,
    key: "smtp",
    label: "E-mail (servidor próprio)",
    description:
      "Integração com caixa de e-mail corporativa via SMTP/IMAP (ex.: Microsoft 365 / Outlook), sem alteração de MX no domínio.",
    icon: (props) => <MailIcon {...props} />,
    color: "#0078D4",
  },
  // ---- Voz / Telefonia ----
  {
    id: CHANNEL_TYPES.PABX,
    key: "telephony",
    label: "Telefonia",
    description:
      "Integração com PABX para abrir solicitações automaticamente ao receber ligações.",
    icon: (props) => <PhoneIcon {...props} />,
  },
  {
    id: CHANNEL_TYPES.TWILIO_PHONE,
    key: "telephony_xpro",
    label: "Telefonia Xpro",
    description: "Telefonia integrada da plataforma para ligações e roteamento.",
    icon: (props) => <PhoneIcon {...props} />,
  },

  // ---- SMS ----
  {
    id: CHANNEL_TYPES.SMS,
    key: "sms",
    label: "SMS",
    description: "Envio e recebimento de SMS pela plataforma. Contrate ou reutilize números pela tela de canais.",
    icon: (props) => <SmsIcon {...props} />,
    color: "#ed6c02",
  },
  {
    id: CHANNEL_TYPES.RCS_BUSINESS,
    key: "rcs_business",
    label: "RCS Business",
    description:
      "Mensagens RCS pela plataforma. Escolha o serviço de mensagens e salve — envio e recebimento são configurados automaticamente.",
    icon: (props) => <ForumIcon {...props} />,
    color: "#0063D3",
  },
//   // ---- Voz ----
//   {
//     id: CHANNEL_TYPES.PABX,
//     key: "pabx",
//     label: "Telefone PABX",
//     description: "Integração com PABX",
//     icon: (props) => <PhoneIcon {...props} />,
//   },

//   // ---- SMS ----
//   {
//     id: CHANNEL_TYPES.TWILIO_SMS,
//     key: "twilio_sms",
//     label: "SMS (legado)",
//     description: "Mensagens SMS via integração legada",
//     icon: (props) => <SmsIcon {...props} />,
//   },
//   {
//     id: CHANNEL_TYPES.CLICKATELL_SMS,
//     key: "clickatell_sms",
//     label: "SMS Clickatell",
//     description: "Mensagens SMS Clickatell",
//     icon: (props) => <SmsIcon {...props} />,
//   },

//   // ---- Apps ----
//   {
//     id: CHANNEL_TYPES.APP_IOS,
//     key: "ios",
//     label: "App iOS",
//     description: "Login e push via iOS",
//     icon: (props) => <AppleIcon {...props} />,
//   },
//   {
//     id: CHANNEL_TYPES.APP_ANDROID,
//     key: "android",
//     label: "App Android",
//     description: "Login e push via Android",
//     icon: (props) => <AndroidIcon {...props} />,
//   },

//   // ---- Web/Ads ----
//   {
//     id: CHANNEL_TYPES.WORDPRESS,
//     key: "wordpress",
//     label: "WordPress",
//     description: "Form/Widget no site WP",
//     icon: (props) => <LanguageIcon {...props} />,
//   },
//   {
//     id: CHANNEL_TYPES.GOOGLE_ADS,
//     key: "google_ads",
//     label: "Google Ads",
//     description: "Leads via Google Ads",
//     icon: (props) => <AdsClickIcon {...props} />,
//   },
//   {
//     id: CHANNEL_TYPES.META_ADS,
//     key: "meta_ads",
//     label: "Meta Ads",
//     description: "Leads via Meta Ads",
//     icon: (props) => <AdsClickIcon {...props} />,
//   },

  // ---- Ecommerce ----
  {
    id: CHANNEL_TYPES.ECOMMERCE,
    key: "ecommerce",
    label: "Ecommerce",
    description: "Pedidos, carrinho e webhooks",
    icon: (props) => <StorefrontIcon {...props} />,
  },
  {
    id: CHANNEL_TYPES.WEBSITE_VIACAO,
    key: "website_viacao",
    label: "Website Viação",
    description: "Website com URL temporária e domínio customizado",
    icon: (props) => <LanguageIcon {...props} />,
    segments: [WorkspaceSegments.PassengerTransport],
  },
  {
    id: CHANNEL_TYPES.XPRO_CLIENTE,
    key: "xpro_cliente",
    label: "Portal do cliente",
    description: "Portal web para clientes abrirem e acompanharem chamados",
    icon: (props) => <SupportAgentIcon {...props} />,
    color: "#0b5cff",
  },
  {
    id: CHANNEL_TYPES.TWILIO_VIDEO,
    key: "twilio_video",
    label: "Chamadas de Vídeo",
    description: "Link de lobby para o cliente; atendente entra pela solicitação no chat",
    icon: (props) => <VideocamIcon {...props} />,
    color: "#c62828",
  },
  {
    id: CHANNEL_TYPES.RECLAME_AQUI,
    key: "reclame_aqui",
    label: "Reclame Aqui",
    description:
      "Monitora reclamações do perfil (scraping) ou integra via API HugMe com resposta no chat",
    icon: (props) => <ReportProblemOutlinedIcon {...props} />,
    color: "#1B5E20",
  },
  {
    id: CHANNEL_TYPES.SITE_WEBCHAT,
    key: "site_webchat",
    label: "Chat no site",
    description: "Botão de conversa no site. O visitante escreve sem sair da página e o atendimento chega no Xpro.",
    icon: (props) => <ChatBubbleOutlineIcon {...props} />,
    color: "#0F6FDE",
  },
];

/** Card sem `segments` vale para todos os workspaces. */
export function channelCardMatchesSegment(card, segment) {
  const segs = card?.segments;
  if (!Array.isArray(segs) || segs.length === 0) return true;
  const seg = normalizeWorkspaceSegment(segment);
  return segs.some((s) => normalizeWorkspaceSegment(s) === seg);
}

export function channelCardsForSegment(segment) {
  return CHANNEL_CARDS.filter((card) => channelCardMatchesSegment(card, segment));
}

export const findCardById = (id) =>
  CHANNEL_CARDS.find((c) => c.id === id) ||
  CHANNEL_CARDS.find((c) => c.id === CHANNEL_TYPES.DEFAULT);

/** Busca card pelo tipo numérico sem fallback (útil para ícones). */
export const findCardByTipo = (tipo) => {
  const n = Number(tipo);
  if (!Number.isFinite(n)) return null;
  return CHANNEL_CARDS.find((c) => c.id === n) ?? null;
};

/** Renderiza ícone do catálogo a partir do tipo numérico do canal. */
export const renderChannelCatalogIcon = (tipo, iconProps = {}) => {
  const card = findCardByTipo(tipo);
  if (!card) return null;
  const { sx: sxProp, ...restProps } = iconProps;
  return card.icon({
    fontSize: "small",
    ...restProps,
    sx: {
      ...(sxProp || {}),
      ...(card.color ? { color: card.color } : {}),
    },
  });
};
